var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Checkbox, ListItemText } from '@mui/material';
import { InputBase, FormControl, FormHelperText, FormLabel, Select, MenuItem, alpha, inputBaseClasses, } from '@mui/material';
import clsx from 'clsx';
import parsePhoneNumber from 'libphonenumber-js';
import MidmarkTypography from '../Typography/MidmarkTypography';
import { CloseIcon, CaretRightIcon } from '../../icons';
const PREFIX = 'MidmarkTextField';
const classes = {
    placeholder: `${PREFIX}-placeholder`,
    select: `${PREFIX}-select`,
    selectIcon: `${PREFIX}-selectIcon`,
    selectIconOpen: `${PREFIX}-selectIconOpen`,
    inputSelectPlaceholder: `${PREFIX}-inputSelectPlaceholder`,
    selectInput: `${PREFIX}-selectInput`,
    selectMenu: `${PREFIX}-selectMenu`,
    selectMenuList: `${PREFIX}-selectMenuList`,
    selectIsOpen: `${PREFIX}-selectIsOpen`,
};
const StyledFormControl = styled(FormControl)(({ theme }) => ({
    [`& .${classes.placeholder}`]: {
        fontStyle: 'italic',
        fontSize: theme.typography.fontSize * 0.9,
    },
    [`&.${classes.select}`]: {
        [`& .${classes.selectInput}`]: {
            padding: '10px 12px',
        },
        [`& .${inputBaseClasses.root}`]: {
            padding: 0,
        },
    },
    [`& .${classes.selectIcon}`]: {
        transform: 'rotate(90deg)',
        width: '20px',
        right: '10px',
        color: 'black',
        transition: `transform ${theme.transitions.duration.short}ms`,
    },
    [`& .${classes.selectIconOpen}`]: {
        transform: 'rotate(-90deg)',
    },
    [`& .${classes.inputSelectPlaceholder}`]: {
        '&[role="button"]': {
            padding: '12px 14px',
            paddingRight: '34px',
            color: alpha(theme.palette.text.primary, 0.46),
        },
    },
    [`&.${classes.selectIsOpen} .${inputBaseClasses.root}`]: {
        backgroundColor: 'white',
        borderColor: 'transparent',
        boxShadow: '0px 3px 6px #00000029',
    },
}));
const StyledSelectModalRoot = styled('div')({
    position: 'fixed',
    zIndex: 1300,
    right: 0,
    bottom: 0,
    top: 0,
    left: 0,
    [`& .${classes.selectMenu}`]: {
        marginTop: '10px',
    },
    [`& .${classes.selectMenuList}`]: {
        padding: 0,
        '& li:not(:first-of-type)': {
            borderTop: '1px solid rgba(231, 231, 231, 1)',
        },
        '& li': {
            borderRadius: 0,
        },
    },
});
const MidmarkInputBase = styled(InputBase)(({ theme }) => ({
    overflow: 'hidden',
    [`& .${inputBaseClasses.inputMultiline}`]: {
        '&::-webkit-scrollbar': {
            width: '8px',
            height: '12px',
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: '20px',
            border: '2px solid white',
            backgroundColor: 'lightgrey',
        },
    },
    'label + &, label + p + &': {
        marginTop: theme.spacing(1.2),
    },
    [`&.${inputBaseClasses.error}, &.${inputBaseClasses.error} div[role="button"]`]: {
        border: `1px solid ${theme.palette.error.main}`,
        color: theme.palette.error.main,
    },
    [`&.${inputBaseClasses.error} svg`]: {
        fill: theme.palette.error.main,
    },
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.grey[500]}`,
    padding: '8px 12px',
    transition: theme.transitions.create([
        'border-color',
        'box-shadow',
        'border',
    ]),
    '&:focus': {
        borderRadius: theme.shape.borderRadius,
    },
    '&.Mui-disabled': {
        border: `1px solid ${theme.palette.grey[300]}`,
    },
}));
const MidmarkTextFieldMultiSelectContext = React.createContext([]);
const useMultiSelectSelectedItems = () => {
    return useContext(MidmarkTextFieldMultiSelectContext);
};
export default function MidmarkTextField(_a) {
    var { autoComplete, autoFocus = false, children, className = '', color = 'primary', defaultValue, disabled = false, error = false, errorMessage, FormHelperTextProps, fullWidth = false, helperText, hiddenLabel, id, InputLabelProps, inputProps, InputProps, inputRef, label, multiline = false, name, maxRows, minRows, onBlur, onChange, onFocus, placeholder, select, SelectProps, required = false, requiredPlaceholderText = 'Required', value, type } = _a, other = __rest(_a, ["autoComplete", "autoFocus", "children", "className", "color", "defaultValue", "disabled", "error", "errorMessage", "FormHelperTextProps", "fullWidth", "helperText", "hiddenLabel", "id", "InputLabelProps", "inputProps", "InputProps", "inputRef", "label", "multiline", "name", "maxRows", "minRows", "onBlur", "onChange", "onFocus", "placeholder", "select", "SelectProps", "required", "requiredPlaceholderText", "value", "type"]);
    // Auto assign an id if a name is provided to get accessibility
    if (name && id === undefined) {
        id = name;
    }
    const [selectIsOpen, setSelectIsOpen] = useState(false);
    const [inputPropsClassName, setInputPropsClassName] = useState(InputProps && InputProps.className);
    const [selectedItems, setSelectedItems] = useState(value ? value : defaultValue ? defaultValue : []);
    const [textValue, setTextValue] = useState(value != null ? value : defaultValue != null ? defaultValue : undefined);
    if (select) {
        className = [...className.split(' '), classes.select].join(' ');
    }
    if (selectIsOpen) {
        className = [...className.split(' '), classes.selectIsOpen].join(' ');
    }
    InputProps = InputProps !== null && InputProps !== void 0 ? InputProps : {};
    InputProps.className = inputPropsClassName;
    const [selectValueHasChanged, setSelectValueHasChanged] = useState(false);
    const helperTextId = helperText && id ? `${id}-helper-text` : undefined;
    const errorTextId = id ? `${id}-error-text` : undefined;
    const inputLabelId = label && id ? `${id}-label` : undefined;
    placeholder =
        required && placeholder
            ? `${placeholder} (${requiredPlaceholderText})`
            : placeholder;
    let selectPlaceholderIsShown = false;
    const isMultiSelect = SelectProps === null || SelectProps === void 0 ? void 0 : SelectProps.multiple;
    if (isMultiSelect) {
        const originalOnChange = onChange;
        onChange = (e) => {
            setSelectedItems(e.target.value);
            if (originalOnChange) {
                originalOnChange(e);
            }
        };
    }
    if (select &&
        !selectValueHasChanged &&
        !defaultValue &&
        (!value || value === '') &&
        placeholder) {
        value = placeholder;
        defaultValue = placeholder;
        const originalInputPropsClass = InputProps.className;
        InputProps.className += ' ' + classes.placeholder;
        const originalOnChange = onChange;
        selectPlaceholderIsShown = true;
        onChange = (e) => {
            setInputPropsClassName(originalInputPropsClass !== null && originalInputPropsClass !== void 0 ? originalInputPropsClass : '');
            setSelectValueHasChanged(true);
            if (isMultiSelect) {
                setSelectedItems(e.target.value);
            }
            if (originalOnChange) {
                originalOnChange(e);
            }
        };
    }
    if (!select && !isMultiSelect) {
        const originalOnChange = onChange;
        onChange = (e) => {
            setTextValue(e.target.value);
            if (e.target.value && type === 'tel') {
                const phoneNumber = parsePhoneNumber(e.target.value, 'US');
                if (phoneNumber && phoneNumber.isValid()) {
                    const formattedPhoneNumber = phoneNumber.formatNational();
                    setTextValue(formattedPhoneNumber);
                    e.target.value = formattedPhoneNumber;
                }
            }
            if (originalOnChange) {
                originalOnChange(e);
            }
        };
    }
    const menuProps = {
        elevation: 3,
        classes: {
            paper: classes.selectMenu,
            list: classes.selectMenuList,
        },
        components: {
            Root: StyledSelectModalRoot,
        },
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
        },
        transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
        },
    };
    const InputElement = (_jsx(MidmarkInputBase, Object.assign({ "data-testid": MIDMARK_TEXT_FIELD_TEST_IDS.textFieldInput, "aria-describedby": `${helperText && helperTextId} ${error && errorTextId}`, autoComplete: autoComplete, autoFocus: autoFocus, defaultValue: defaultValue, fullWidth: fullWidth, multiline: multiline, name: name, maxRows: maxRows, minRows: minRows, required: required, type: type, value: textValue, id: id, classes: {
            input: clsx([
                selectPlaceholderIsShown
                    ? classes.inputSelectPlaceholder
                    : '',
                select ? classes.selectInput : '',
            ]),
        }, inputRef: inputRef, onBlur: onBlur, onChange: onChange, onFocus: onFocus, placeholder: placeholder, inputProps: inputProps }, InputProps)));
    const FormControlElement = (_jsxs(StyledFormControl, Object.assign({ className: className, disabled: disabled, error: error, fullWidth: fullWidth, hiddenLabel: hiddenLabel, required: required, color: color }, other, { children: [label && (_jsx(FormLabel, Object.assign({ htmlFor: id, id: inputLabelId, focused: false, disabled: false, sx: { marginLeft: 0, maxHeight: '1.3rem' } }, InputLabelProps, { children: _jsx(MidmarkTypography, Object.assign({ variant: "overlineMedium" }, { children: label })) }))), helperText && (_jsx(FormHelperText, Object.assign({ "data-testid": MIDMARK_TEXT_FIELD_TEST_IDS.textFieldHelperText, id: helperTextId, sx: (theme) => (Object.assign(Object.assign({}, theme.typography.caption), { marginLeft: 0, color: theme.palette.text.hint })) }, FormHelperTextProps, { children: helperText }))), select ? (_jsxs(Select, Object.assign({ "data-testid": MIDMARK_TEXT_FIELD_TEST_IDS.textFieldSelect, "aria-describedby": `${helperTextId} ${errorTextId}`, id: id, labelId: inputLabelId, value: value, input: InputElement, IconComponent: CaretRightIcon, classes: {
                    icon: classes.selectIcon,
                    iconOpen: classes.selectIconOpen,
                }, MenuProps: menuProps }, SelectProps, { onOpen: (e) => {
                    setSelectIsOpen(true);
                    if (SelectProps === null || SelectProps === void 0 ? void 0 : SelectProps.onOpen) {
                        SelectProps === null || SelectProps === void 0 ? void 0 : SelectProps.onOpen(e);
                    }
                }, onClose: (e) => {
                    setSelectIsOpen(false);
                    if (SelectProps === null || SelectProps === void 0 ? void 0 : SelectProps.onClose) {
                        SelectProps.onClose(e);
                    }
                } }, { children: [children, placeholder &&
                        (SelectProps && SelectProps.native ? (_jsx("option", Object.assign({ style: { display: 'none' }, disabled: true, value: placeholder }, { children: placeholder }))) : (_jsx(MenuItem, Object.assign({ style: { display: 'none' }, disabled: true, value: placeholder }, { children: placeholder }))))] }))) : (InputElement), _jsx(FormHelperText, Object.assign({ role: "alert", id: errorTextId, sx: { marginLeft: 0 } }, FormHelperTextProps, { children: error ? errorMessage : ' ' }))] })));
    return SelectProps && SelectProps.multiple ? (_jsx(MidmarkTextFieldMultiSelectContext.Provider, Object.assign({ value: selectedItems }, { children: FormControlElement }))) : (FormControlElement);
}
/**
 * TODO: Finish multi-select component
 */
const MultiSelectMenuItem = (_a) => {
    var { value, key, children } = _a, others = __rest(_a, ["value", "key", "children"]);
    if (value == null && others['data-value']) {
        value = others['data-value'];
    }
    const selectedItems = useMultiSelectSelectedItems();
    return (_jsxs(MenuItem, Object.assign({ value: value }, others, { children: [_jsx(ListItemText, { children: children }), _jsx(Checkbox, { checkedIcon: _jsxs("span", Object.assign({ style: {
                        display: 'flex',
                        alignItems: 'inherit',
                        justifyContent: 'inherit',
                        position: 'relative',
                    } }, { children: [_jsx("span", { style: {
                                position: 'absolute',
                                width: '70%',
                                height: '70%',
                                backgroundColor: '#30A8DF',
                            } }), _jsx(CloseIcon, { style: {
                                zIndex: 1,
                                color: 'white',
                                transform: 'scale(0.75)',
                            } })] })), checked: selectedItems != null &&
                    Array.isArray(selectedItems) &&
                    selectedItems.indexOf(value) > -1 })] }), key));
};
MultiSelectMenuItem.displayName = 'MultiSelectMenuItem';
MidmarkTextField.MultiSelectMenuItem = MultiSelectMenuItem;
export var MIDMARK_TEXT_FIELD_TEST_IDS;
(function (MIDMARK_TEXT_FIELD_TEST_IDS) {
    MIDMARK_TEXT_FIELD_TEST_IDS["textFieldInput"] = "text-field-input";
    MIDMARK_TEXT_FIELD_TEST_IDS["textFieldSelect"] = "text-field-select";
    MIDMARK_TEXT_FIELD_TEST_IDS["textFieldHelperText"] = "text-field-helper-text";
})(MIDMARK_TEXT_FIELD_TEST_IDS || (MIDMARK_TEXT_FIELD_TEST_IDS = {}));
